import React from "react";
import { Link, useParams } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Card, CardContent } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useSEO } from "@/hooks/useSEO";
import { articleRouteMeta, canonicalUrl } from "@/seo/routeMeta";
import { getArticleBySlug, statsArticles } from "@/data/statsArticles";
import EmailCapture from "@/components/EmailCapture";
import NotFound from "./NotFound";

const StatsArticlePage = () => {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  const meta = article ? articleRouteMeta(article) : undefined;

  useSEO({
    title: meta?.title ?? "US Dating Statistics",
    description: meta?.description ?? "",
    keywords: meta?.keywords,
    canonical: article ? canonicalUrl(`/stats/${article.slug}`) : undefined,
    structuredData: meta?.structuredData,
  });

  if (!article) {
    return <NotFound />;
  }

  const relatedArticles = article.related
    .map((relatedSlug) => statsArticles.find((a) => a.slug === relatedSlug))
    .filter((a): a is NonNullable<typeof a> => Boolean(a));

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <Breadcrumbs />

      <div className="container mx-auto px-4 py-8">
        <article className="max-w-3xl mx-auto space-y-10">
          <header className="space-y-4">
            <h1 className="text-4xl font-bold tracking-tight">{article.title}</h1>
            <p className="text-sm text-muted-foreground">
              Published {article.datePublished} · Female Partner Calculator research
            </p>
            <Card className="bg-primary/5 border-primary/30">
              <CardContent className="pt-6 text-center">
                <div className="text-5xl font-extrabold text-primary">
                  {article.heroStat.value}
                </div>
                <div className="text-muted-foreground mt-2">{article.heroStat.label}</div>
              </CardContent>
            </Card>
            {article.intro.map((paragraph, i) => (
              <p key={i} className="text-lg text-muted-foreground leading-relaxed">
                {paragraph}
              </p>
            ))}
          </header>

          <main className="space-y-10">
            {article.sections.map((section, i) => (
              <section key={i} className="space-y-4">
                <h2 className="text-2xl font-semibold">{section.heading}</h2>
                {section.paragraphs.map((paragraph, j) => (
                  <p key={j} className="leading-relaxed">
                    {paragraph}
                  </p>
                ))}
                {section.list && (
                  <ul className="list-disc pl-6 space-y-2">
                    {section.list.map((item, j) => (
                      <li key={j}>{item}</li>
                    ))}
                  </ul>
                )}
                {section.table && (
                  <Table>
                    <TableCaption>{section.table.caption}</TableCaption>
                    <TableHeader>
                      <TableRow>
                        {section.table.headers.map((header, j) => (
                          <TableHead key={j}>{header}</TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {section.table.rows.map((row, j) => (
                        <TableRow key={j}>
                          {row.map((cell, k) => (
                            <TableCell key={k}>{cell}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </section>
            ))}

            {/* Calculator CTA */}
            <section className="bg-muted/50 rounded-lg p-6 text-center space-y-3">
              <h2 className="text-xl font-semibold">Run the numbers on your own standards</h2>
              <p className="text-muted-foreground">
                Combine this statistic with age, education, body type, and more filters to
                see your personal match probability.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/" className="text-primary hover:underline font-medium">
                  Female Partner Calculator →
                </Link>
                <Link to="/ideal-woman-calculator" className="text-primary hover:underline font-medium">
                  Ideal Woman Calculator →
                </Link>
                <Link to="/male-partner-calculator" className="text-primary hover:underline font-medium">
                  Male Partner Calculator →
                </Link>
                <Link to="/dating-standards-calculator" className="text-primary hover:underline font-medium">
                  Dating Standards Test →
                </Link>
              </div>
            </section>

            {/* FAQ */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold">Frequently asked questions</h2>
              {/* Default-open so answers are present in prerendered HTML */}
              <Accordion
                type="multiple"
                defaultValue={article.faq.map((_, i) => `faq-${i}`)}
                className="w-full"
              >
                {article.faq.map((item, i) => (
                  <AccordionItem key={i} value={`faq-${i}`}>
                    <AccordionTrigger>{item.question}</AccordionTrigger>
                    <AccordionContent>{item.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>

            <EmailCapture />

            {/* Sources */}
            <section className="space-y-3">
              <h2 className="text-2xl font-semibold">Sources</h2>
              <ul className="space-y-2 text-sm">
                {article.sources.map((source, i) => (
                  <li key={i}>
                    <a
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      {source.title}
                    </a>
                  </li>
                ))}
              </ul>
            </section>

            {/* Related */}
            {relatedArticles.length > 0 && (
              <section className="space-y-3 border-t pt-8">
                <h2 className="text-xl font-semibold">Related statistics</h2>
                <ul className="space-y-2">
                  {relatedArticles.map((related) => (
                    <li key={related.slug}>
                      <Link
                        to={`/stats/${related.slug}`}
                        className="text-primary hover:underline"
                      >
                        {related.title}
                      </Link>
                    </li>
                  ))}
                </ul>
                <p className="text-sm text-muted-foreground">
                  Or browse the full{" "}
                  <Link to="/stats" className="text-primary hover:underline">
                    US dating statistics hub
                  </Link>
                  .
                </p>
              </section>
            )}
          </main>
        </article>
      </div>

      <Footer />
    </div>
  );
};

export default StatsArticlePage;
